/**
 * Reusable Modal Component
 * A flexible modal dialog that can be customized with CSS classes
 */
class Modal {
    constructor(options = {}) {
        this.isOpen = false;
        this.options = {
            closeOnBackdrop: true,
            closeOnEsc: true,
            width: '600px',
            ...options,
        };
        this.backdrop = this.createBackdrop();
        this.element = this.createModal();
        this.contentContainer = this.element.querySelector('.modal-content');
        this.setupEventListeners();
    }
    createBackdrop() {
        const backdrop = document.createElement('div');
        backdrop.className = 'modal-backdrop';
        backdrop.style.cssText = `
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      z-index: 1000;
    `;
        return backdrop;
    }
    createModal() {
        const modal = document.createElement('div');
        modal.className = `modal ${this.options.className || ''}`.trim();
        modal.style.cssText = `
      display: none;
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: white;
      border-radius: 8px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);
      z-index: 1001;
      width: ${this.options.width};
      max-width: 90vw;
      max-height: 90vh;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    `;
        const header = document.createElement('div');
        header.className = 'modal-header';
        header.style.cssText = `
      padding: 20px;
      border-bottom: 1px solid #e5e7eb;
      display: flex;
      justify-content: space-between;
      align-items: center;
    `;
        const title = document.createElement('h2');
        title.className = 'modal-title';
        title.textContent = this.options.title || '';
        title.style.cssText = `
      margin: 0;
      font-size: 1.5rem;
      font-weight: 600;
      color: #111827;
    `;
        const closeBtn = document.createElement('button');
        closeBtn.className = 'modal-close';
        closeBtn.innerHTML = '&times;';
        closeBtn.style.cssText = `
      background: none;
      border: none;
      font-size: 2rem;
      cursor: pointer;
      color: #6b7280;
      padding: 0;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      transition: background 0.2s;
    `;
        closeBtn.onmouseenter = () => closeBtn.style.background = '#f3f4f6';
        closeBtn.onmouseleave = () => closeBtn.style.background = 'none';
        closeBtn.onclick = () => this.close();
        header.appendChild(title);
        header.appendChild(closeBtn);
        const content = document.createElement('div');
        content.className = 'modal-content';
        content.style.cssText = `
      padding: 20px;
      overflow-y: auto;
      flex: 1;
    `;
        modal.appendChild(header);
        modal.appendChild(content);
        return modal;
    }
    setupEventListeners() {
        if (this.options.closeOnBackdrop) {
            this.backdrop.addEventListener('click', () => this.close());
        }
        if (this.options.closeOnEsc) {
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && this.isOpen) {
                    this.close();
                }
            });
        }
    }
    /**
     * Set the modal title
     */
    setTitle(title) {
        const titleElement = this.element.querySelector('.modal-title');
        if (titleElement) {
            titleElement.textContent = title;
        }
    }
    /**
     * Set the modal content (HTML string or HTMLElement)
     */
    setContent(content) {
        if (typeof content === 'string') {
            this.contentContainer.innerHTML = content;
        }
        else {
            this.contentContainer.innerHTML = '';
            this.contentContainer.appendChild(content);
        }
    }
    /**
     * Get the content container element
     */
    getContentElement() {
        return this.contentContainer;
    }
    /**
     * Open the modal
     */
    open() {
        if (this.isOpen)
            return;
        if (!this.backdrop.parentElement) {
            document.body.appendChild(this.backdrop);
        }
        if (!this.element.parentElement) {
            document.body.appendChild(this.element);
        }
        this.backdrop.style.display = 'block';
        this.element.style.display = 'flex';
        this.isOpen = true;
        if (this.options.onOpen) {
            this.options.onOpen();
        }
    }
    /**
     * Close the modal
     */
    close() {
        if (!this.isOpen)
            return;
        this.backdrop.style.display = 'none';
        this.element.style.display = 'none';
        this.isOpen = false;
        if (this.options.onClose) {
            this.options.onClose();
        }
    }
    /**
     * Toggle modal open/closed
     */
    toggle() {
        if (this.isOpen) {
            this.close();
        }
        else {
            this.open();
        }
    }
    /**
     * Check if modal is currently open
     */
    isModalOpen() {
        return this.isOpen;
    }
    /**
     * Destroy the modal and remove from DOM
     */
    destroy() {
        this.close();
        if (this.backdrop.parentElement) {
            this.backdrop.parentElement.removeChild(this.backdrop);
        }
        if (this.element.parentElement) {
            this.element.parentElement.removeChild(this.element);
        }
    }
}

/**
 * Prompt Modal Component
 * Simple modal for getting text input from user
 */
class PromptModal {
    constructor(options) {
        this.inputElement = null;
        this.resolveCallback = null;
        this.options = {
            confirmText: 'OK',
            cancelText: 'Cancel',
            ...options,
        };
        this.modal = new Modal({
            title: this.options.title,
            width: '400px',
            className: 'prompt-modal',
            closeOnBackdrop: false,
            closeOnEsc: true,
            onClose: () => this.handleCancel(),
        });
        this.render();
    }
    render() {
        const container = document.createElement('div');
        container.className = 'prompt-container';
        // Message
        const message = document.createElement('p');
        message.textContent = this.options.message;
        message.style.cssText = `
      margin: 0 0 16px 0;
      color: #374151;
      font-size: 14px;
    `;
        // Input
        this.inputElement = document.createElement('input');
        this.inputElement.type = 'text';
        this.inputElement.placeholder = this.options.placeholder || '';
        this.inputElement.value = this.options.defaultValue || '';
        this.inputElement.style.cssText = `
      width: 100%;
      padding: 10px 12px;
      border: 1px solid #d1d5db;
      border-radius: 4px;
      font-size: 14px;
      margin-bottom: 20px;
      box-sizing: border-box;
      transition: border-color 0.2s;
    `;
        this.inputElement.onfocus = () => {
            if (this.inputElement) {
                this.inputElement.style.borderColor = '#3b82f6';
            }
        };
        this.inputElement.onblur = () => {
            if (this.inputElement) {
                this.inputElement.style.borderColor = '#d1d5db';
            }
        };
        // Handle Enter key
        this.inputElement.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                this.handleConfirm();
            }
        });
        // Buttons
        const buttons = document.createElement('div');
        buttons.style.cssText = `
      display: flex;
      justify-content: flex-end;
      gap: 8px;
    `;
        const cancelButton = document.createElement('button');
        cancelButton.textContent = this.options.cancelText;
        cancelButton.style.cssText = `
      padding: 8px 16px;
      border: 1px solid #d1d5db;
      background: white;
      color: #374151;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      transition: background 0.2s;
    `;
        cancelButton.onmouseenter = () => cancelButton.style.background = '#f9fafb';
        cancelButton.onmouseleave = () => cancelButton.style.background = 'white';
        cancelButton.addEventListener('click', () => this.handleCancel());
        const confirmButton = document.createElement('button');
        confirmButton.textContent = this.options.confirmText;
        confirmButton.style.cssText = `
      padding: 8px 16px;
      border: none;
      background: #3b82f6;
      color: white;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      transition: background 0.2s;
    `;
        confirmButton.onmouseenter = () => confirmButton.style.background = '#2563eb';
        confirmButton.onmouseleave = () => confirmButton.style.background = '#3b82f6';
        confirmButton.addEventListener('click', () => this.handleConfirm());
        buttons.appendChild(cancelButton);
        buttons.appendChild(confirmButton);
        container.appendChild(message);
        container.appendChild(this.inputElement);
        container.appendChild(buttons);
        this.modal.setContent(container);
    }
    handleConfirm() {
        const value = this.inputElement?.value || '';
        if (this.resolveCallback) {
            this.resolveCallback(value);
            this.resolveCallback = null;
        }
        this.modal.close();
    }
    handleCancel() {
        if (this.resolveCallback) {
            this.resolveCallback(null);
            this.resolveCallback = null;
        }
        this.modal.close();
    }
    /**
     * Open the modal and get user input
     * Returns a promise that resolves with the input value or null if cancelled
     */
    prompt() {
        return new Promise((resolve) => {
            this.resolveCallback = resolve;
            this.modal.open();
            // Focus input after modal opens
            setTimeout(() => {
                this.inputElement?.focus();
                this.inputElement?.select();
            }, 100);
        });
    }
    /**
     * Destroy the modal
     */
    destroy() {
        this.modal.destroy();
    }
}

/**
 * Load Modal Component
 * Displays items from storage with pagination and filtering
 */
class LoadModal {
    constructor(options) {
        this.currentPage = 1;
        this.filterText = '';
        this.totalPages = 1;
        this.totalItems = 0;
        this.storage = options.storage;
        this.options = {
            itemsPerPage: 10,
            ...options,
        };
        this.modal = new Modal({
            title: 'Load from Local Storage',
            width: '700px',
            className: 'load-modal',
        });
    }
    /**
     * Open the modal and load items
     */
    async open() {
        await this.render();
        this.modal.open();
    }
    /**
     * Close the modal
     */
    close() {
        this.modal.close();
    }
    /**
     * Render the modal content
     */
    async render() {
        const container = document.createElement('div');
        container.className = 'load-modal-container';
        // Search/Filter section
        const filterSection = this.createFilterSection();
        container.appendChild(filterSection);
        // Items list
        const itemsList = await this.createItemsList();
        container.appendChild(itemsList);
        // Pagination
        const pagination = this.createPagination();
        container.appendChild(pagination);
        this.modal.setContent(container);
    }
    createFilterSection() {
        const section = document.createElement('div');
        section.className = 'filter-section';
        section.style.cssText = `
      margin-bottom: 20px;
    `;
        const input = document.createElement('input');
        input.type = 'text';
        input.placeholder = 'Search items...';
        input.value = this.filterText;
        input.className = 'filter-input';
        input.style.cssText = `
      width: 100%;
      padding: 10px 12px;
      border: 1px solid #d1d5db;
      border-radius: 4px;
      font-size: 14px;
      transition: border-color 0.2s;
    `;
        input.onfocus = () => input.style.borderColor = '#3b82f6';
        input.onblur = () => input.style.borderColor = '#d1d5db';
        input.addEventListener('input', (e) => {
            this.filterText = e.target.value;
            this.currentPage = 1;
            this.render();
        });
        section.appendChild(input);
        return section;
    }
    async createItemsList() {
        const container = document.createElement('div');
        container.className = 'items-list';
        container.style.cssText = `
      max-height: 400px;
      overflow-y: auto;
      border: 1px solid #e5e7eb;
      border-radius: 4px;
      margin-bottom: 20px;
    `;
        try {
            // Get items with pagination
            const result = await this.storage.list({
                page: this.currentPage,
                limit: this.options.itemsPerPage || 10,
                filter: this.filterText
                    ? (item) => {
                        const searchText = this.filterText.toLowerCase();
                        return Object.values(item).some((val) => String(val).toLowerCase().includes(searchText));
                    }
                    : undefined,
            });
            this.totalPages = result.totalPages;
            this.totalItems = result.total;
            if (result.items.length === 0) {
                container.innerHTML = `
          <div style="padding: 40px; text-align: center; color: #6b7280;">
            No items found
          </div>
        `;
                return container;
            }
            result.items.forEach((item) => {
                const itemElement = this.createItemElement(item);
                container.appendChild(itemElement);
            });
        }
        catch (error) {
            container.innerHTML = `
        <div style="padding: 40px; text-align: center; color: #ef4444;">
          Error loading items: ${error instanceof Error ? error.message : 'Unknown error'}
        </div>
      `;
        }
        return container;
    }
    createItemElement(item) {
        const element = document.createElement('div');
        element.className = 'item-row';
        element.style.cssText = `
      padding: 12px 16px;
      border-bottom: 1px solid #e5e7eb;
      cursor: pointer;
      transition: background 0.2s;
      display: flex;
      justify-content: space-between;
      align-items: center;
    `;
        element.onmouseenter = () => element.style.background = '#f9fafb';
        element.onmouseleave = () => element.style.background = 'none';
        const content = document.createElement('div');
        content.style.cssText = `
      flex: 1;
      overflow: hidden;
    `;
        if (this.options.formatItem) {
            content.innerHTML = this.options.formatItem(item);
        }
        else if (this.options.displayFields) {
            const fields = this.options.displayFields
                .map((field) => `<strong>${String(field)}:</strong> ${item[field]}`)
                .join(' | ');
            content.innerHTML = fields;
        }
        else {
            content.innerHTML = `<pre style="margin: 0; font-size: 12px; white-space: pre-wrap;">${JSON.stringify(item, null, 2)}</pre>`;
        }
        const loadButton = document.createElement('button');
        loadButton.textContent = 'Load';
        loadButton.className = 'load-item-btn';
        loadButton.style.cssText = `
      background: #3b82f6;
      color: white;
      border: none;
      padding: 6px 16px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      transition: background 0.2s;
      margin-left: 12px;
    `;
        loadButton.onmouseenter = () => loadButton.style.background = '#2563eb';
        loadButton.onmouseleave = () => loadButton.style.background = '#3b82f6';
        loadButton.addEventListener('click', (e) => {
            e.stopPropagation();
            if (this.options.onItemSelect) {
                this.options.onItemSelect(item);
            }
            this.close();
        });
        element.appendChild(content);
        element.appendChild(loadButton);
        // Also allow clicking the row itself
        element.addEventListener('click', () => {
            if (this.options.onItemSelect) {
                this.options.onItemSelect(item);
            }
            this.close();
        });
        return element;
    }
    createPagination() {
        const container = document.createElement('div');
        container.className = 'pagination';
        container.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-top: 12px;
      border-top: 1px solid #e5e7eb;
    `;
        const info = document.createElement('div');
        info.style.cssText = `
      color: #6b7280;
      font-size: 14px;
    `;
        const start = (this.currentPage - 1) * (this.options.itemsPerPage || 10) + 1;
        const end = Math.min(this.currentPage * (this.options.itemsPerPage || 10), this.totalItems);
        info.textContent = `Showing ${start}-${end} of ${this.totalItems} items`;
        const controls = document.createElement('div');
        controls.style.cssText = `
      display: flex;
      gap: 8px;
    `;
        const prevButton = document.createElement('button');
        prevButton.textContent = 'Previous';
        prevButton.disabled = this.currentPage === 1;
        prevButton.style.cssText = `
      padding: 6px 12px;
      border: 1px solid #d1d5db;
      background: ${this.currentPage === 1 ? '#f3f4f6' : 'white'};
      color: ${this.currentPage === 1 ? '#9ca3af' : '#374151'};
      border-radius: 4px;
      cursor: ${this.currentPage === 1 ? 'not-allowed' : 'pointer'};
      font-size: 14px;
      transition: background 0.2s;
    `;
        if (this.currentPage > 1) {
            prevButton.onmouseenter = () => prevButton.style.background = '#f9fafb';
            prevButton.onmouseleave = () => prevButton.style.background = 'white';
        }
        prevButton.addEventListener('click', () => {
            if (this.currentPage > 1) {
                this.currentPage--;
                this.render();
            }
        });
        const pageInfo = document.createElement('span');
        pageInfo.style.cssText = `
      padding: 6px 12px;
      color: #374151;
      font-size: 14px;
    `;
        pageInfo.textContent = `Page ${this.currentPage} of ${this.totalPages}`;
        const nextButton = document.createElement('button');
        nextButton.textContent = 'Next';
        nextButton.disabled = this.currentPage === this.totalPages;
        nextButton.style.cssText = `
      padding: 6px 12px;
      border: 1px solid #d1d5db;
      background: ${this.currentPage === this.totalPages ? '#f3f4f6' : 'white'};
      color: ${this.currentPage === this.totalPages ? '#9ca3af' : '#374151'};
      border-radius: 4px;
      cursor: ${this.currentPage === this.totalPages ? 'not-allowed' : 'pointer'};
      font-size: 14px;
      transition: background 0.2s;
    `;
        if (this.currentPage < this.totalPages) {
            nextButton.onmouseenter = () => nextButton.style.background = '#f9fafb';
            nextButton.onmouseleave = () => nextButton.style.background = 'white';
        }
        nextButton.addEventListener('click', () => {
            if (this.currentPage < this.totalPages) {
                this.currentPage++;
                this.render();
            }
        });
        controls.appendChild(prevButton);
        controls.appendChild(pageInfo);
        controls.appendChild(nextButton);
        container.appendChild(info);
        container.appendChild(controls);
        return container;
    }
    /**
     * Destroy the modal
     */
    destroy() {
        this.modal.destroy();
    }
}

export { LoadModal, Modal, PromptModal };
//# sourceMappingURL=modals.esm.js.map
